/**
 * DOM helpers and the components shared across screens.
 *
 * No framework: this app is a dozen screens with no shared client state beyond
 * the current session object, and a framework would be more code than it saves
 * (CLAUDE.md).
 */

import type { CycleView } from "./types.ts";

type Attrs = Record<string, string | number | boolean | ((event: Event) => void) | undefined>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
	tag: K,
	attrs: Attrs = {},
	...children: Child[]
): HTMLElementTagNameMap[K] {
	const node = document.createElement(tag);
	for (const [key, value] of Object.entries(attrs)) {
		if (value === undefined || value === false) continue;
		if (key.startsWith("on") && typeof value === "function") {
			node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
		} else if (key === "class") {
			node.className = String(value);
		} else if (key === "text") {
			node.textContent = String(value);
		} else if (key === "html") {
			node.innerHTML = String(value);
		} else {
			node.setAttribute(key, value === true ? "" : String(value));
		}
	}
	for (const child of children) {
		if (child === null || child === undefined || child === false) continue;
		node.append(typeof child === "string" ? document.createTextNode(child) : child);
	}
	return node;
}

/** Portuguese carries lang="pt-BR" so VoiceOver pronounces it (docs/05). */
export function pt(text: string, className = "pt"): HTMLElement {
	return el("p", { class: className, lang: "pt-BR", text });
}

export function ptSpan(text: string, className = "pt"): HTMLElement {
	return el("span", { class: className, lang: "pt-BR", text });
}

export function en(text: string, className = "en"): HTMLElement {
	return el("p", { class: className, lang: "en", text });
}

/**
 * The English apparatus. At english_ratio below 0.3 it collapses to a tappable
 * `+` that reveals on demand rather than disappearing entirely.
 */
export function gloss(text: string, ratio: number): HTMLElement | null {
	if (text.trim() === "") return null;
	if (ratio >= 0.3) return el("div", { class: "gloss", lang: "en", text });

	const wrapper = el("div");
	const button = el("button", {
		class: "gloss-toggle",
		type: "button",
		"aria-expanded": "false",
		text: "+ English",
	});
	button.addEventListener("click", () => {
		const shown = button.getAttribute("aria-expanded") === "true";
		button.setAttribute("aria-expanded", shown ? "false" : "true");
		if (shown) {
			wrapper.querySelector(".gloss")?.remove();
			button.textContent = "+ English";
		} else {
			wrapper.append(el("div", { class: "gloss", lang: "en", text }));
			button.textContent = "− English";
		}
	});
	wrapper.append(button);
	return wrapper;
}

/** Section rule: a single tilde row (docs/05, use 2 of 3). */
export function ruleTil(count = 5): HTMLElement {
	return el("div", { class: "rule-til", "aria-hidden": "true", text: new Array(count).fill("~").join(" ") });
}

/**
 * Session progress: a row of tildes filled left to right. Not a bar
 * (docs/05, use 1 of 3).
 */
export function tildeProgress(done: number, total: number): HTMLElement {
	const filled = Math.max(0, Math.min(done, total));
	return el(
		"div",
		{
			class: "til",
			role: "progressbar",
			"aria-valuemin": "0",
			"aria-valuemax": String(total),
			"aria-valuenow": String(filled),
			"aria-label": `Block ${filled} of ${total}`,
		},
		el("b", { text: "~".repeat(filled) }),
		el("i", { text: "~".repeat(Math.max(0, total - filled)) }),
	);
}

export function statTile(label: string, value: string): HTMLElement {
	return el(
		"div",
		{ class: "stat" },
		el("div", { class: "stat__label", text: label }),
		el("div", { class: "stat__value", text: value }),
	);
}

export function bar(fraction: number, color = "var(--azul)"): HTMLElement {
	const pct = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
	return el("div", { class: "bar" }, el("span", { style: `width:${pct}%;background:${color}` }));
}

/**
 * The cycle graphic. Eleven slots, always: L L L L R L L L L R T — four
 * lessons, a review, four lessons, a review, then the test.
 */
export function cycleStrip(cycle: CycleView): HTMLElement {
	const row = el("div", { class: "cycle", role: "list", "aria-label": "Session cycle" });

	for (const slot of cycle.slots) {
		const label = slot.kind === "review" ? "R" : slot.kind === "test" ? "T" : String(slot.ordinal);
		const classes = ["cycle__slot"];
		if (label.length > 2) classes.push("cycle__slot--wide");
		if (slot.kind === "review") classes.push("cycle__slot--review");
		if (slot.kind === "test") classes.push("cycle__slot--test");
		if (slot.status === "done") classes.push("cycle__slot--done");
		if (slot.status === "now") classes.push("cycle__slot--now");

		row.append(
			el("span", {
				class: classes.join(" "),
				role: "listitem",
				title: `Session ${slot.ordinal} · ${slot.kind}`,
				"aria-label": `Session ${slot.ordinal}, ${slot.kind}, ${slot.status}`,
				text: label,
			}),
		);
	}

	return row;
}

export interface ShellOptions {
	eyebrowLeft: string;
	eyebrowRight?: string;
	/** Leading link out of the screen. Today is where Progress and Settings live. */
	back?: { label: string; onClick: () => void };
	progress?: { done: number; total: number };
	meta?: string;
	testChrome?: boolean;
	body: Node[];
	footer?: Node;
}

/**
 * The layout every screen uses: 56pt sticky header, scrolling body, sticky
 * footer holding the one primary action in the thumb zone.
 *
 * There is deliberately no clock in the header — the phone already shows one.
 */
export function shell(options: ShellOptions): DocumentFragment {
	const fragment = document.createDocumentFragment();

	const header = el(
		"header",
		{ class: options.testChrome === true ? "hdr hdr--test" : "hdr" },
		el(
			"div",
			{ class: "eyebrow" },
			el(
				"div",
				{ class: "eyebrow__lead" },
				options.back === undefined
					? null
					: el("button", {
							class: "crumb",
							type: "button",
							onclick: options.back.onClick,
							text: `‹ ${options.back.label}`,
						}),
				el("span", { text: options.eyebrowLeft }),
			),
			options.eyebrowRight === undefined ? null : el("span", { text: options.eyebrowRight }),
		),
		options.progress === undefined ? null : tildeProgress(options.progress.done, options.progress.total),
		options.meta === undefined ? null : el("div", { class: "meta", style: "margin-top:6px", text: options.meta }),
	);

	const body = el("main", { class: "body" });
	body.append(...options.body);

	fragment.append(header, body);
	if (options.footer !== undefined) {
		fragment.append(el("footer", { class: "foot" }, options.footer));
	}
	return fragment;
}

export function primaryButton(
	label: string,
	onClick: () => void,
	options: { disabled?: boolean; variant?: "ghost" | "ochre" } = {},
): HTMLButtonElement {
	const classes = ["btn"];
	if (options.variant === "ghost") classes.push("btn--ghost");
	if (options.variant === "ochre") classes.push("btn--ochre");
	return el("button", {
		class: classes.join(" "),
		type: "button",
		disabled: options.disabled === true,
		onclick: onClick,
		text: label,
	});
}

/** Sentence case with no exclamation marks; numbers are always labelled. */
export function percent(value: number): string {
	return `${Math.round(value * 100)}%`;
}

export function mount(node: Node): void {
	const app = document.getElementById("app");
	if (app === null) return;
	app.replaceChildren(node);
	window.scrollTo(0, 0);
}

export function toast(message: string, tone: "ochre" | "brick" = "ochre"): HTMLElement {
	return el("div", { class: tone === "brick" ? "notice notice--brick" : "notice", lang: "en", text: message });
}
